// 课程管理(博颂 2026-09-30):行政班下「添加课程」、课程「学生」名单增减。
// 之前课程只能由整理/校宝导入产生,也没有改课程学生的入口。

import { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

import {
  type HomeroomCourse,
  type Student,
  useClassWithRoster,
  useCreateCourse,
  useSetCourseStudents,
  useSubjects,
} from "../../api/school";

function Modal({ title, onClose, children, testId }: { title: string; onClose: () => void; children: React.ReactNode; testId: string }) {
  // 卡片内联 modal 必须 portal 到 body,否则会被带 opacity/transform 的祖先困住。
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4" onClick={onClose}>
      <div className="flex max-h-[85vh] w-full max-w-lg flex-col rounded-xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()} data-testid={testId} role="dialog" aria-label={title}>
        <div className="border-b border-slate-200 px-4 py-3 text-sm font-semibold text-slate-800">{title}</div>
        {children}
      </div>
    </div>,
    document.body,
  );
}

export function AddCourseModal({ homeroomId, homeroomName, semester, existing, onClose }: {
  homeroomId: string;
  homeroomName: string;
  semester: string | null;
  existing: HomeroomCourse[];
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const subjects = useSubjects();
  const create = useCreateCourse(homeroomId);
  const taken = useMemo(() => new Set(existing.filter((c) => c.course_type !== "tier").map((c) => c.subject)), [existing]);
  // 职能学科(如招生)不是课程。
  const options = (subjects.data ?? []).filter((s) => s.status !== "archived" && s.role_type !== "functional" && !taken.has(s.slug));
  const [slug, setSlug] = useState("");
  const [name, setName] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const picked = options.find((s) => s.slug === slug);

  const submit = async () => {
    if (!slug) return;
    setErr(null);
    try {
      await create.mutateAsync({ subject_slug: slug, name: name.trim() || null, semester });
      onClose();
    } catch (e) {
      setErr((e as Error).message);
    }
  };

  return (
    <Modal title={t("classes.courses.add.title", { name: homeroomName })} onClose={onClose} testId="add-course-modal">
      <div className="space-y-3 px-4 py-3 text-sm">
        <label className="block">
          <span className="text-xs text-slate-500">{t("classes.courses.add.subject")}</span>
          <select id="add-course-subject" value={slug} onChange={(e) => setSlug(e.target.value)} className="mt-1 w-full rounded border border-slate-300 px-2 py-1.5">
            <option value="">{t("classes.courses.add.pickSubject")}</option>
            {options.map((s) => <option key={s.slug} value={s.slug}>{s.name}</option>)}
          </select>
          {options.length === 0 && !subjects.isLoading && <span className="mt-1 block text-xs text-slate-400">{t("classes.courses.add.noneLeft")}</span>}
        </label>
        <label className="block">
          <span className="text-xs text-slate-500">{t("classes.courses.add.name")}</span>
          <input id="add-course-name" value={name} onChange={(e) => setName(e.target.value)} placeholder={picked?.name ?? ""} className="mt-1 w-full rounded border border-slate-300 px-2 py-1.5" />
        </label>
        <p className="text-xs text-slate-500">{t("classes.courses.add.hint", { semester: semester ?? "—" })}</p>
        {err && <p className="text-xs text-rose-600" role="alert">{err}</p>}
      </div>
      <div className="flex justify-end gap-2 border-t border-slate-100 px-4 py-3 text-sm">
        <button type="button" onClick={onClose} className="rounded px-3 py-1.5 text-slate-600 hover:bg-slate-50">{t("common.cancel")}</button>
        <button type="button" onClick={() => void submit()} disabled={!slug || create.isPending} className="rounded bg-sky-500 px-3 py-1.5 text-white hover:bg-sky-600 disabled:opacity-50">
          {create.isPending ? t("common.saving") : t("classes.courses.add.submit")}
        </button>
      </div>
    </Modal>
  );
}

export function CourseStudentsModal({ course, homeroomId, homeroomStudents, onClose }: {
  course: { id: string; name: string };
  homeroomId: string;
  homeroomStudents: Student[];
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const roster = useClassWithRoster(course.id);
  const save = useSetCourseStudents(course.id, homeroomId);
  const [picked, setPicked] = useState<Set<string> | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const current = useMemo(() => new Set((roster.data?.students ?? []).map((s) => s.id)), [roster.data]);
  const sel = picked ?? current;
  // 行政班学生 + 课程里已有但不在本行政班的学生(例如分层课程从别班加入的),都列出来。
  const list = useMemo(() => {
    const seen = new Set(homeroomStudents.map((s) => s.id));
    const extra = (roster.data?.students ?? []).filter((s) => !seen.has(s.id));
    return [...homeroomStudents, ...extra];
  }, [homeroomStudents, roster.data]);
  const toggle = (id: string) => {
    const next = new Set(sel);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setPicked(next);
  };
  const added = [...sel].filter((id) => !current.has(id)).length;
  const removed = [...current].filter((id) => !sel.has(id)).length;

  const submit = async () => {
    setErr(null);
    try {
      await save.mutateAsync([...sel]);
      onClose();
    } catch (e) {
      setErr((e as Error).message);
    }
  };

  return (
    <Modal title={t("classes.courses.roster.title", { name: course.name })} onClose={onClose} testId="course-students-modal">
      <div className="flex items-center justify-between border-b border-slate-100 px-4 py-2 text-xs text-slate-500">
        <span>{t("classes.courses.roster.count", { n: sel.size, total: list.length })}</span>
        <span className="flex gap-3">
          <button type="button" className="text-sky-600 hover:underline" onClick={() => setPicked(new Set(list.map((s) => s.id)))}>{t("classes.courses.roster.all")}</button>
          <button type="button" className="text-sky-600 hover:underline" onClick={() => setPicked(new Set())}>{t("classes.courses.roster.none")}</button>
        </span>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-2">
        {roster.isLoading ? (
          <p className="py-4 text-sm text-slate-500">{t("common.loading")}</p>
        ) : (
          <ul className="divide-y divide-slate-100 text-sm">
            {list.map((s) => (
              <li key={s.id}>
                <label className="flex cursor-pointer items-center gap-2 py-1.5">
                  <input type="checkbox" checked={sel.has(s.id)} onChange={() => toggle(s.id)} aria-label={s.name_zh} />
                  <span className="text-slate-800">{s.name_zh}</span>
                  {s.name_en && <span className="text-slate-400">{s.name_en}</span>}
                  {!homeroomStudents.some((h) => h.id === s.id) && <span className="ml-auto text-xs text-violet-600">{t("classes.courses.roster.otherClass")}</span>}
                </label>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="flex items-center justify-between gap-2 border-t border-slate-100 px-4 py-3 text-sm">
        <span className="text-xs text-slate-500">
          {added || removed ? t("classes.courses.roster.diff", { added, removed }) : ""}
          {removed > 0 && <span className="ml-1 text-slate-400">{t("classes.courses.roster.keepScores")}</span>}
        </span>
        <span className="flex gap-2">
          {err && <span className="self-center text-xs text-rose-600" role="alert">{err}</span>}
          <button type="button" onClick={onClose} className="rounded px-3 py-1.5 text-slate-600 hover:bg-slate-50">{t("common.cancel")}</button>
          <button type="button" onClick={() => void submit()} disabled={save.isPending || (!added && !removed)} className="rounded bg-sky-500 px-3 py-1.5 text-white hover:bg-sky-600 disabled:opacity-50">
            {save.isPending ? t("common.saving") : t("common.save")}
          </button>
        </span>
      </div>
    </Modal>
  );
}
